import type { IRecord } from '@platform/core/types';
import type { IBiBar } from './IBiBar';
import type { IBiModuleInput } from './IBiModuleInput';
import type { IBiStats } from './IBiStats';

const DAY = 86400000;
const TOP = 8;

const dueOf = (r: IRecord): string => (typeof r.values['due'] === 'string' ? r.values['due'].slice(0, 10) : '');
const isLate = (r: IRecord, today: string): boolean => !r.isClosed && !!dueOf(r) && dueOf(r) < today;

/** Смуги «найбільше — перше» (до 8), ширина — частка від найбільшого значення. */
export function bars(pairs: Array<[string, number]>): IBiBar[] {
  const top = [...pairs].sort((a, b) => b[1] - a[1]).slice(0, TOP);
  const max = Math.max(1, ...top.map((p) => p[1]));
  return top.map(([label, count]) => ({ label, count, width: Math.round((count / max) * 100) }));
}

/** Кількість за виконавцем (`unassigned` — без виконавця). */
function byAssignee(list: IRecord[], unassigned: string): Array<[string, number]> {
  const out = new Map<string, number>();
  list.forEach((r) => {
    const a = r.values['assignee'];
    const name = a && typeof a === 'object' && !Array.isArray(a) ? a.name : unassigned;
    out.set(name, (out.get(name) ?? 0) + 1);
  });
  return [...out];
}

/**
 * Зведення бізнес-аналітики (чиста функція, прототип v4): записи головних таблиць модулів; «разом» модуля — лічильник каталогу, решта —
 * за завантаженими записами; виконано — закриті записи, оновлені за останні 30 днів; тижні — 8 відрізків по 7 днів до `now`.
 */
export function biStats(records: readonly IRecord[], modules: readonly IBiModuleInput[], now: number, today: string, unassigned: string): IBiStats {
  const open = records.filter((r) => !r.isClosed);
  const late = open.filter((r) => isLate(r, today));
  const since30 = now - 30 * DAY;
  const updatedAfter = (r: IRecord, t: number) => Date.parse(r.updatedAt) >= t;
  const of = (key: string) => records.filter((r) => r.module === key);
  const weeks = Array.from({ length: 8 }, (_, i) => {
    const ago = 7 - i;
    const end = now - ago * 7 * DAY;
    const start = end - 7 * DAY;
    const inWeek = (iso: string) => { const t = Date.parse(iso); return t >= start && t < end; };
    return { ago, created: records.filter((r) => inWeek(r.createdAt)).length, done: records.filter((r) => r.isClosed && inWeek(r.updatedAt)).length };
  });
  return {
    records: modules.reduce((sum, m) => sum + m.total, 0),
    open: open.length,
    overdue: late.length,
    done30: records.filter((r) => r.isClosed && updatedAfter(r, since30)).length,
    modules: modules.map((m) => {
      const rs = of(m.key);
      const o = rs.filter((r) => !r.isClosed);
      return { key: m.key, name: m.name, total: m.total, open: o.length, overdue: o.filter((r) => isLate(r, today)).length,
        donePct: rs.length ? Math.round(((rs.length - o.length) / rs.length) * 100) : 0 };
    }),
    people: bars(byAssignee(open, unassigned)),
    peopleOverdue: bars(byAssignee(late, unassigned)),
    moduleOpen: bars(modules.map((m) => [m.name, open.filter((r) => r.module === m.key).length])).filter((b) => b.count > 0),
    weeks,
    active: bars(modules.map((m) => [m.name, of(m.key).filter((r) => updatedAfter(r, since30)).length])).filter((b) => b.count > 0),
  };
}
